import {
  deleteNotificationContact as deleteNotificationContactApi,
  ensureNotificationContacts as ensureNotificationContactsApi,
  getGuestNotificationContacts as getGuestNotificationContactsApi,
  getNotificationContactPicker as getNotificationContactPickerApi,
  getNotificationContacts as getNotificationContactsApi,
  linkGuestNotificationContact as linkGuestNotificationContactApi,
  unlinkGuestNotificationContact as unlinkGuestNotificationContactApi,
  putNotificationContact as putNotificationContactApi,
  resendNotificationContactVerification as resendNotificationContactVerificationApi,
} from "@/lib/api/clients/NotificationContactClient";
import { handleErrorServerWithStatus, type ServerError } from "@/lib/api/errors/ErrorHandler";
import type {
  EnsureNotificationContactsRequest,
  EnsureNotificationContactsResponse,
  GuestNotificationContactLink,
  GuestNotificationContacts,
  NotificationContact,
  NotificationContactList,
  NotificationContactPicker,
  NotificationContactRegistryUpsert,
  ResendVerificationResult,
} from "@/types/NotificationContact";

/**
 * Handlers return the standard `ServerError` envelope instead of throwing
 * (the page pattern of `EventHandling`). `handleErrorServerWithStatus` keeps
 * the HTTP status, so the page can tell the 429 confirm-mail cap from a 400.
 */
export async function getNotificationContacts(
  eventId: string,
): Promise<NotificationContactList | ServerError> {
  try {
    return await getNotificationContactsApi(eventId);
  } catch (error) {
    return handleErrorServerWithStatus(error);
  }
}

/** The registry body only — see `putNotificationContact`, which sends exactly its four keys. */
export async function upsertNotificationContact(
  eventId: string,
  body: NotificationContactRegistryUpsert,
): Promise<NotificationContact | ServerError> {
  try {
    return await putNotificationContactApi(eventId, body);
  } catch (error) {
    return handleErrorServerWithStatus(error);
  }
}

export async function removeNotificationContact(
  eventId: string,
  contactId: string,
): Promise<void | ServerError> {
  try {
    await deleteNotificationContactApi(eventId, contactId);
  } catch (error) {
    return handleErrorServerWithStatus(error);
  }
}

export async function resendNotificationContactVerification(
  eventId: string,
  contactId: string,
): Promise<ResendVerificationResult | ServerError> {
  try {
    return await resendNotificationContactVerificationApi(eventId, contactId);
  } catch (error) {
    return handleErrorServerWithStatus(error);
  }
}

export async function getGuestNotificationContacts(
  eventId: string,
  guestId: string,
): Promise<GuestNotificationContacts | ServerError> {
  try {
    return await getGuestNotificationContactsApi(eventId, guestId);
  } catch (error) {
    return handleErrorServerWithStatus(error);
  }
}

export async function linkGuestNotificationContact(
  eventId: string,
  guestId: string,
  body: GuestNotificationContactLink,
): Promise<NotificationContact | ServerError> {
  try {
    return await linkGuestNotificationContactApi(eventId, guestId, body);
  } catch (error) {
    return handleErrorServerWithStatus(error);
  }
}

export async function unlinkGuestNotificationContact(
  eventId: string,
  contactId: string,
  guestId: string,
): Promise<void | ServerError> {
  try {
    await unlinkGuestNotificationContactApi(eventId, contactId, guestId);
  } catch (error) {
    return handleErrorServerWithStatus(error);
  }
}

export async function ensureNotificationContacts(
  eventId: string,
  body: EnsureNotificationContactsRequest,
): Promise<EnsureNotificationContactsResponse | ServerError> {
  try {
    return await ensureNotificationContactsApi(eventId, body);
  } catch (error) {
    return handleErrorServerWithStatus(error);
  }
}

/** plan arrival-automation Rev. 4 §1.4 — see `getNotificationContactPicker`. */
export async function getNotificationContactPicker(
  eventId: string,
): Promise<NotificationContactPicker | ServerError> {
  try {
    return await getNotificationContactPickerApi(eventId);
  } catch (error) {
    return handleErrorServerWithStatus(error);
  }
}
